/**
 * Computes how many category chips fit on a single row before overflowing the
 * rest into the "More" dropdown.
 *
 * When every chip fits, all are visible and no space is reserved for the More
 * button. Otherwise the More button is guaranteed to render, so its width (plus
 * one gap) is subtracted from the budget before greedily fitting chips from the
 * start of the list. At least one chip stays visible so the row is never empty.
 *
 * @param {Object} params
 * @param {number[]} params.chipWidths - Rendered width of each chip, in order.
 * @param {number} params.moreWidth - Rendered width of the More dropdown trigger.
 * @param {number} params.available - Available width of the row container.
 * @param {number} params.gap - Horizontal gap between adjacent items.
 * @returns {number} Number of chips to show before the overflow split.
 */
const getVisibleCount = ({ chipWidths, moreWidth, available, gap }) => {
  if (chipWidths.length === 0) return 0

  const totalWidth = chipWidths.reduce(
    (sum, width, index) => sum + width + (index > 0 ? gap : 0),
    0,
  )

  if (totalWidth <= available) return chipWidths.length

  const budget = available - moreWidth - gap
  let used = 0
  let count = 0

  for (let index = 0; index < chipWidths.length; index += 1) {
    used += chipWidths[index] + (index > 0 ? gap : 0)
    if (used > budget) break
    count += 1
  }

  return Math.max(count, 1)
}

export default getVisibleCount
